
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useBranding } from "@/contexts/branding-context"
import { getTenantConfig } from "./tenant-configs"
import { getCurrentTenantId, getTenantFromSubdomain } from "@/lib/tenant"
import { SEOHead } from "@/components/seo-head"
import { useMemo, useState } from "react"

export function TenantLandingPage() {
  const { logoUrl, businessName, primaryColor } = useBranding()
  const [language, setLanguage] = useState<"sq" | "en">("sq")

  // Get tenant-specific configuration
  const config = useMemo(() => {
    const tenantId = getCurrentTenantId()
    const subdomain = getTenantFromSubdomain()
    return getTenantConfig(tenantId || subdomain || "")
  }, [])

  // Replace placeholders with actual business name
  const hero = useMemo(
    () => ({
      title: config.hero.title.replace("{{businessName}}", businessName),
      subtitle: config.hero.subtitle.replace("{{businessName}}", businessName),
      description: config.hero.description.replace("{{businessName}}", businessName),
    }),
    [config, businessName]
  )

  const seo = useMemo(() => {
    const title = config.seo?.title || `${businessName} - Fitness`
    const description = config.seo?.description || config.hero.description.replace("{{businessName}}", businessName)
    const ogImage = config.seo?.ogImage || config.tenantLogoUrl || undefined
    return { title, description, ogImage }
  }, [config, businessName])

  const hasFlexProContent =
    config.landingStyle === "flexPro" &&
    !!config.heroBilingual &&
    !!config.ctaBilingual &&
    !!config.locations?.length &&
    !!config.pricingHighlights?.length &&
    !!config.socialLinks?.length

  const formatAllPrice = (price: number) => `${new Intl.NumberFormat("sq-AL").format(price)} ALL`
  const pickText = (sq: string, en: string) => (language === "sq" ? sq : en)
  const heroImage = config.locations?.[0]?.imageUrl || "/locations/flex-gym-tirane.jpg"
  const tenantLogo = config.tenantLogoUrl || logoUrl
  const planGroups = useMemo(() => {
    const plans = config.pricingHighlights || []
    return {
      gymTirane: plans.filter((plan) => plan.locationKey === "gymTirane"),
      sportClub: plans.filter((plan) => plan.locationKey === "sportClub"),
      greenCoast: plans.filter((plan) => plan.locationKey === "greenCoast"),
    }
  }, [config.pricingHighlights])
  const durationOrder = ["daily", "fixed", "monthly", "twoMonths", "threeMonths", "sixMonths", "annual"] as const
  const durationLabel = (key: (typeof durationOrder)[number]) =>
    ({
      daily: pickText("Ditore", "Daily"),
      fixed: pickText("Seanca", "Sessions"),
      monthly: pickText("Mujore", "Monthly"),
      twoMonths: pickText("2 Mujore", "2 Months"),
      threeMonths: pickText("3 Mujore", "3 Months"),
      sixMonths: pickText("6 Mujore", "6 Months"),
      annual: pickText("Vjetore", "Annual"),
    }[key])

  if (!hasFlexProContent) {
    return <DefaultTenantLanding hero={hero} config={config} businessName={businessName} logoUrl={logoUrl} primaryColor={primaryColor} seo={seo} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-secondary/20">
      <SEOHead title={seo.title} description={seo.description} ogImage={seo.ogImage} themeColor={primaryColor} />
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {tenantLogo ? (
              <img src={tenantLogo} alt={`${businessName} Logo`} className="h-10 w-10 rounded-md object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-md border" />
            )}
            <div>
              <p className="text-sm font-semibold">{businessName}</p>
              <p className="text-xs text-muted-foreground">{pickText("Premium Fitness", "Premium Fitness")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config.flexKitchen ? (
              <a
                href={config.flexKitchen.url}
                target="_blank"
                rel="noreferrer"
                className="hidden rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-500/20 md:inline-flex"
              >
                Flex Kitchen
              </a>
            ) : null}
            <div className="rounded-md border p-1">
              <button
                type="button"
                onClick={() => setLanguage("sq")}
                className={`rounded px-2 py-1 text-xs ${language === "sq" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                SQ
              </button>
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`rounded px-2 py-1 text-xs ${language === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                EN
              </button>
            </div>
            <Button size="sm" variant="outline" asChild>
              <Link to="/login">{pickText("Hyr", "Login")}</Link>
            </Button>
            <Button size="sm" asChild style={{ backgroundColor: primaryColor }} className="hover:opacity-90">
              <Link to="/register">{pickText("Regjistrohu", "Register")}</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative min-h-[78vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/60" />
        <div className="container relative z-10 mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div className="space-y-6 text-white">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/80">Flex Group</p>
                <h1 className="text-4xl font-bold tracking-tight md:text-6xl">
                  {pickText(config.heroBilingual?.titleAl || "", config.heroBilingual?.titleEn || "")}
                </h1>
                <p className="text-xl md:text-2xl text-white/95">
                  {pickText(config.heroBilingual?.subtitleAl || "", config.heroBilingual?.subtitleEn || "")}
                </p>
                <p className="max-w-xl text-base text-white/85 md:text-lg">
                  {pickText(config.heroBilingual?.descriptionAl || "", config.heroBilingual?.descriptionEn || "")}
                </p>
                {config.flexKitchen ? (
                  <p className="text-sm font-medium text-white/90">
                    {pickText("Tani edhe Flex Kitchen:", "Now with Flex Kitchen:")} {config.flexKitchen.handle}
                  </p>
                ) : null}
                <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                  <Button size="lg" asChild style={{ backgroundColor: primaryColor }} className="hover:opacity-90">
                    <Link to="/register">
                      {pickText(config.ctaBilingual?.primaryAl || "", config.ctaBilingual?.primaryEn || "")}
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild className="border-white/40 bg-white/10 text-white hover:bg-white/20">
                    <Link to="/login">
                      {pickText(config.ctaBilingual?.secondaryAl || "", config.ctaBilingual?.secondaryEn || "")}
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <Card className="border-white/20 bg-white/10 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="text-white">{pickText("3 Lokacione", "3 Locations")}</CardTitle>
                    <CardDescription className="text-white/80">
                      {pickText("Performance dhe lifestyle premium.", "Performance and premium lifestyle.")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-white/90">
                    {config.locations?.map((location) => (
                      <div key={location.name} className="flex items-center justify-between rounded-md bg-white/10 px-3 py-2">
                        <span>{location.name}</span>
                        <span>{location.city}</span>
                      </div>
                    ))}
                    {config.flexKitchen ? (
                      <a
                        href={config.flexKitchen.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between rounded-md border border-red-400/40 bg-red-500/20 px-3 py-2 font-medium text-red-100 transition hover:bg-red-500/30"
                      >
                        <span>Flex Kitchen</span>
                        <span>{config.flexKitchen.handle}</span>
                      </a>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-8 md:py-14">
        <div className="mb-8 space-y-2 text-center">
          <h2 className="text-3xl font-bold md:text-4xl">{pickText("Lokacionet tona", "Our Locations")}</h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            {pickText(
              "Zgjidh ambientin qe pershtatet me objektivin tend.",
              "Choose the location that best matches your goals."
            )}
          </p>
        </div>
        <div
          className={`mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 ${
            config.flexKitchen ? "lg:grid-cols-4" : "lg:grid-cols-3"
          }`}
        >
          {config.locations?.map((location) => (
            <Card key={location.name} className="group h-full overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div
                className="h-44 w-full rounded-t-xl border-b bg-cover bg-center"
                style={{
                  backgroundImage: location.imageUrl
                    ? `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.25)), url(${location.imageUrl})`
                    : "linear-gradient(135deg, hsl(var(--secondary)), hsl(var(--muted)))",
                }}
              />
              <CardHeader>
                <CardTitle>{location.name}</CardTitle>
                <CardDescription>{location.city}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">{pickText(location.focusAl, location.focusEn)}</p>
                <a
                  href={location.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  {location.instagramHandle}
                </a>
              </CardContent>
            </Card>
          ))}
          {config.flexKitchen ? (
            <Card className="group h-full overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div
                className="h-44 w-full rounded-t-xl border-b bg-cover bg-center"
                style={{
                  backgroundImage: config.flexKitchen.imageUrl
                    ? `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.25)), url(${config.flexKitchen.imageUrl})`
                    : "linear-gradient(135deg, hsl(var(--secondary)), hsl(var(--muted)))",
                }}
              />
              <CardHeader>
                <CardTitle>Flex Kitchen</CardTitle>
                <CardDescription>{pickText("Nutrition", "Nutrition")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">{pickText(config.flexKitchen.descriptionAl, config.flexKitchen.descriptionEn)}</p>
                <a
                  href={config.flexKitchen.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  {config.flexKitchen.handle}
                </a>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>

      <section className="container mx-auto px-4 py-10 md:py-16">
        <div className="mb-8 space-y-2 text-center">
          <h2 className="text-3xl font-bold md:text-4xl">{pickText("Planet Kryesore", "Membership Highlights")}</h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            {pickText(
              "Cmimet e kuruara nga planet aktive ne sistem, te organizuara sipas lokacionit dhe sherbimit.",
              "Curated prices from active plans, organized by location and service."
            )}
          </p>
        </div>
        <Tabs defaultValue="gymTirane" className="mx-auto max-w-6xl">
          <div className="flex justify-center">
            <TabsList className="h-auto flex-wrap gap-2 border border-red-500/30 bg-zinc-900/90 p-2 text-zinc-300">
              <TabsTrigger value="gymTirane" className="text-zinc-300 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                {pickText("Flex Gym Tirane", "Flex Gym Tirana")}
              </TabsTrigger>
              <TabsTrigger value="sportClub" className="text-zinc-300 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                {pickText("Flex Classes & Sport Club", "Flex Classes & Sport Club")}
              </TabsTrigger>
              <TabsTrigger value="greenCoast" className="text-zinc-300 data-[state=active]:bg-red-600 data-[state=active]:text-white">
                {pickText("Flex Green Coast", "Flex Green Coast")}
              </TabsTrigger>
            </TabsList>
          </div>

          {(["gymTirane", "sportClub", "greenCoast"] as const).map((groupKey) => (
            <TabsContent key={groupKey} value={groupKey}>
              <div className="overflow-x-auto pt-4 pb-2">
                <div className="flex min-w-max items-start gap-4">
                  {durationOrder.map((durationKey) => {
                    const durationPlans = planGroups[groupKey].filter((plan) => plan.durationKey === durationKey)
                    if (!durationPlans.length) return null

                    return (
                      <div
                        key={durationKey}
                        className="w-[320px] shrink-0 rounded-xl bg-card/70"
                      >
                        <div className="flex items-center gap-2 px-4 py-3">
                          <div className="h-5 w-1.5 rounded-full bg-red-600" />
                          <h3 className="text-sm font-semibold uppercase tracking-wide">{durationLabel(durationKey)}</h3>
                        </div>
                        <div className="space-y-3 p-3">
                          {durationPlans.map((plan) => (
                            <Card
                              key={`${plan.labelAl}-${plan.periodAl}`}
                              className="relative overflow-hidden border border-red-500/25 bg-card/90 shadow-[0_14px_34px_-24px_rgba(239,68,68,0.75)] transition duration-300 hover:border-red-500/45 hover:shadow-[0_20px_36px_-24px_rgba(239,68,68,0.95)]"
                            >
                              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-700 via-red-500 to-red-700" />
                              <CardHeader>
                                <CardTitle className="pt-1 text-base leading-snug">
                                  {pickText(plan.labelAl, plan.labelEn)}
                                </CardTitle>
                                <CardDescription className="pt-1">{pickText(plan.periodAl, plan.periodEn)}</CardDescription>
                              </CardHeader>
                              <CardContent className="space-y-3 pt-1">
                                <p className="text-3xl font-bold tracking-tight text-red-600">{formatAllPrice(plan.price)}</p>
                                {plan.noteAl || plan.noteEn ? (
                                  <p className="text-xs text-muted-foreground">
                                    {pickText(plan.noteAl || "", plan.noteEn || "")}
                                  </p>
                                ) : null}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <section className="container mx-auto px-4 py-8 md:py-14">
        <div className="rounded-2xl border bg-card p-6 shadow-sm md:p-10">
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">
              {pickText("Na ndiq ne Instagram", "Follow us on Instagram")}
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-muted-foreground">
              {pickText(
                "Ndiq energjine e secilit lokacion dhe zgjidh komunitetin tend te preferuar.",
                "Follow each location and pick your favorite community."
              )}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {config.socialLinks?.map((social) => (
              <Card key={social.handle} className="h-full transition duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{social.name}</CardTitle>
                  <CardDescription>{social.handle}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm">{pickText(social.descriptionAl, social.descriptionEn)}</p>
                  <a
                    href={social.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex text-sm font-semibold hover:underline"
                    style={{ color: primaryColor }}
                  >
                    {pickText("Hap Instagram", "Open Instagram")}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {config.flexKitchen ? (
        <section className="container mx-auto px-4 py-8 md:py-14">
          <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
            <div className="grid gap-0 md:grid-cols-2">
              <div
                className="min-h-64 bg-cover bg-center"
                style={{
                  backgroundImage: config.flexKitchen.imageUrl
                    ? `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(${config.flexKitchen.imageUrl})`
                    : "linear-gradient(135deg, hsl(var(--secondary)), hsl(var(--muted)))",
                }}
              />
              <div className="space-y-5 p-8 md:p-10">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Flex Kitchen</p>
                <h3 className="text-3xl font-bold">{pickText("Nutrition qe mban ritmin tend", "Nutrition that keeps your pace")}</h3>
                <p className="text-muted-foreground">{pickText(config.flexKitchen.descriptionAl, config.flexKitchen.descriptionEn)}</p>
                <a
                  href={config.flexKitchen.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-semibold hover:underline"
                  style={{ color: primaryColor }}
                >
                  {pickText("Ndiq Flex Kitchen", "Follow Flex Kitchen")} ({config.flexKitchen.handle})
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="container mx-auto px-4 py-16 text-center">
        <div className="mx-auto max-w-3xl space-y-5 rounded-2xl border bg-card/70 p-8 shadow-sm backdrop-blur">
          <h2 className="text-3xl font-bold md:text-4xl">
            {pickText("Gati per hapin e radhes?", "Ready for your next step?")}
          </h2>
          <p className="text-muted-foreground">
            {pickText(
              "Regjistrohu online per te nisur menjehere ose hyn ne panelin tend nese je anetar ekzistues.",
              "Register online to get started now or log in if you are already a member."
            )}
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button size="lg" asChild style={{ backgroundColor: primaryColor }} className="hover:opacity-90">
              <Link to="/register">
                {pickText(config.ctaBilingual?.primaryAl || "", config.ctaBilingual?.primaryEn || "")}
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/login">
                {pickText(config.ctaBilingual?.secondaryAl || "", config.ctaBilingual?.secondaryEn || "")}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-4 py-8 text-sm text-muted-foreground md:flex-row">
          <p>
            &copy; 2026 {businessName}.{" "}
            {pickText("Te gjitha te drejtat e rezervuara.", "All rights reserved.")}
          </p>
          <div className="flex items-center gap-2">
            <div className="rounded-md border p-1">
              <button
                type="button"
                onClick={() => setLanguage("sq")}
                className={`rounded px-2 py-1 text-xs ${language === "sq" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                SQ
              </button>
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`rounded px-2 py-1 text-xs ${language === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function DefaultTenantLanding({
  hero,
  config,
  businessName,
  logoUrl,
  primaryColor,
  seo,
}: {
  hero: { title: string; subtitle: string; description: string }
  config: ReturnType<typeof getTenantConfig>
  businessName: string
  logoUrl: string | null
  primaryColor: string
  seo: { title: string; description: string; ogImage?: string }
}) {
  const tenantLogo = config.tenantLogoUrl || logoUrl

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <SEOHead title={seo.title} description={seo.description} ogImage={seo.ogImage} themeColor={primaryColor} />
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="text-center space-y-8 max-w-4xl mx-auto">
          {tenantLogo && (
            <div className="flex justify-center">
              <div className="relative w-32 h-32 md:w-40 md:h-40">
                <img src={tenantLogo} alt={`${businessName} Logo`} className="w-full h-full object-contain" loading="eager" />
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">{hero.title}</h1>
            <p className="text-xl md:text-2xl text-muted-foreground">{hero.subtitle}</p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{hero.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" asChild style={{ backgroundColor: primaryColor }} className="hover:opacity-90">
              <Link to="/register">{config.cta.primary}</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/login">{config.cta.secondary}</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold">What We Offer</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to reach your fitness goals
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {config.features.map((feature, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 bg-secondary/30 rounded-lg my-16">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold">Why Choose {businessName}?</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">More than just a gym - a lifestyle transformation</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="space-y-4">
            {config.benefits.slice(0, Math.ceil(config.benefits.length / 2)).map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1 h-5 w-5 rounded-full flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                <div>
                  <h3 className="font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {config.benefits.slice(Math.ceil(config.benefits.length / 2)).map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1 h-5 w-5 rounded-full flex-shrink-0" style={{ backgroundColor: primaryColor }} />
                <div>
                  <h3 className="font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Ready to Get Started?</h2>
          <p className="text-lg text-muted-foreground">
            Take the first step towards a healthier, stronger you. Join {businessName} today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" asChild style={{ backgroundColor: primaryColor }} className="hover:opacity-90">
              <Link to="/register">{config.cta.primary}</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/login">{config.cta.secondary}</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; 2026 {businessName}. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/support" className="hover:text-foreground transition-colors">
                Support
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
